'use client';

/**
 * Account Login Component
 *
 * Handles user login with validation, error handling, and optional "Remember Me" functionality.
 *
 * @param {Function} onLoginSuccess - Optional callback executed after successful login.
 * @returns {JSX.Element} The login component.
 */

// React & Core Hook Imports
import React, { useState, useMemo, useEffect } from 'react';

// Third-Party Library Imports
import { useForm, useWatch, SubmitHandler, FieldErrors } from 'react-hook-form';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { v4 as uuidv4 } from 'uuid';
import { Eye, EyeOff } from 'lucide-react';

// Component Imports
import Toast from '@/components/toast';
import SubmitButton from '@/components/submitButton';
import CheckBox from '@/components/checkbox';

// Context Imports
import { getSession } from 'next-auth/react';
import { useOrder } from '@/lib/context/orderContext';

// Function Imports
import { migrateOrder } from '@/lib/functions/migrateOrder';
import { logMessage } from '@/lib/functions/logMessage';
import {
  getRememberedLoginEmail,
  setRememberedLoginEmail,
} from '@/lib/functions/rememberLoginEmail';

// Style Imports
import styles from './accountLogin.module.scss';

// Constants Imports
import { REGEX } from '@/lib/constants/common';

const LABELS = {
  EMAIL: 'Email Address',
  PASSWORD: 'Password',
  REMEMBER_ME: 'Remember Me',
};

// Form Fields Configuration
type Inputs = {
  login_email: string;
  login_password: string;
  login_rememberme: boolean;
};

interface AccountLoginProps {
  onLoginSuccess?: () => void; // Optional callback executed after successful login
}

const FORM_FIELDS = [
  {
    id: 'login_email',
    label: LABELS.EMAIL,
    errorMessage: 'Please provide a valid email address.',
    registerOptions: {
      required: 'Email is required',
      pattern: REGEX.EMAIL,
    },
    type: 'text',
    autocomplete: 'email',
  },
  {
    id: 'login_password',
    label: LABELS.PASSWORD,
    errorMessage: 'Please enter a valid password.',
    registerOptions: {
      required: 'Password is required',
    },
    type: 'password',
    autocomplete: 'current-password',
  },
] as const;

export default function AccountLogin({ onLoginSuccess }: AccountLoginProps) {
  const { state: orderState } = useOrder();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    clearErrors,
    setValue,
  } = useForm<Inputs>({
    defaultValues: {
      login_email: '',
      login_password: '',
      login_rememberme: false,
    },
  });

  // Watch form fields
  const loginEmail = useWatch({ control, name: 'login_email', defaultValue: '' });
  const loginPassword = useWatch({ control, name: 'login_password', defaultValue: '' });
  const rememberMe = useWatch({ control, name: 'login_rememberme', defaultValue: false });

  // Derive form validity from watched values
  const isFormValid = useMemo(() => {
    return !!loginEmail && !!loginPassword;
  }, [loginEmail, loginPassword]);

  useEffect(() => {
    const rememberedEmail = getRememberedLoginEmail();
    if (rememberedEmail) {
      setValue('login_email', rememberedEmail);
      setValue('login_rememberme', true);
    }
  }, [setValue]);

  /**
   * Validates form fields and sets error messages accordingly.
   */
  const validateFields = (errors: FieldErrors<Inputs>) => {
    const missing = FORM_FIELDS
      .filter(({ id }) => errors[id as keyof Inputs]?.type === 'required')
      .map(({ label }) => label);

    const invalid = FORM_FIELDS
      .filter(({ id }) => errors[id as keyof Inputs] && errors[id as keyof Inputs]?.type !== 'required')
      .map(({ label }) => label);

    if (missing.length) setErrorMessage('Please enter all required fields.');
    if (invalid.length) setErrorMessage(`The following fields are invalid: ${invalid.join(', ')}`);
  };

  /**
   * Handles login form submission.
   */
  const onLogin: SubmitHandler<Inputs> = async (data) => {
    clearErrors();
    setErrorMessage(null);

    try {
      const response = await signIn('credentials', {
        email: data.login_email,
        password: data.login_password,
        redirect: false,
      });

      if (response?.error) {
        console.error(response?.error);
        const errorMap: Record<number, string> = {
          401: 'Invalid email or password.',
          502: 'There was an authentication error. Please try signing in with your registered email address.',
        };

        const errorMessage = errorMap[response.status] ?? response.error;
        setErrorMessage(errorMessage);
      } else {
        setRememberedLoginEmail(data.login_rememberme ? data.login_email : null);

        // Attempt migrating the order, so that it is
        // correctly associated with the logged-in user
        try {
          const orderToken = orderState.token;
          const session = await getSession();
          const authToken = session?.token;

          if (orderToken && authToken) {
            await migrateOrder(orderToken, authToken);
          }
        } catch (err) {
          console.error('Order migration failed after login:', err);
        }

        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }
    } catch {
      logMessage('Unexpected login error', 'error');
    }
  };

  return (
    <div className={styles.wrapper}>
      <form onSubmit={handleSubmit(onLogin, validateFields)} noValidate>
        {FORM_FIELDS.map(({ id, label, errorMessage, registerOptions, type, autocomplete }) => (
          <div className={'formInput formText'} key={id}>
            <label htmlFor={id}>
              {label} {registerOptions.required && <span className={styles.required}>*</span>}
            </label>
            <div className={id === 'login_password' ? styles.passwordWrapper : styles.inputWrapper}>
              <input
                id={id}
                type={id === 'login_password' && showPassword ? 'text' : type}
                aria-invalid={errors[id as keyof Inputs] ? 'true' : 'false'}
                aria-describedby={errors[id as keyof Inputs] ? `${id}-error` : undefined}
                {...register(id as keyof Inputs, registerOptions)}
                disabled={isSubmitting}
                autoComplete={autocomplete}
              />
              {id === 'login_password' && (
                <button
                  type="button"
                  className={styles.toggleVisibility}
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              )}
            </div>
            {errors[id as keyof Inputs] && errorMessage && (
              <span id={`${id}-error`} className={styles.inlineValidationError}>
                {errorMessage}
              </span>
            )}
          </div>
        ))}

        <div className={styles.checkboxWrapper}>
          <CheckBox
            id="login_rememberme"
            label={LABELS.REMEMBER_ME}
            data={{ parameter: 'rememberme', value: 'true' }}
            isChecked={() => rememberMe}
            onCheck={() => {
              setValue('login_rememberme', !rememberMe);
            }}
            disabled={isSubmitting}
          />
        </div>

        <div>
          <SubmitButton
            label="Login"
            loadingLabel="Logging In"
            loading={isSubmitting}
            disabled={!isFormValid}
          />
        </div>

        <div className={styles.forgotPassword}>
          <Link href="/my-account/lost-password">Lost your Password?</Link>
        </div>
      </form>
      <div role="alert" aria-atomic="true">
        {errorMessage && <Toast id={uuidv4()} type="error" label={true} message={errorMessage} />}
      </div>
    </div>
  );
}
